import { useState } from "react";
import { Download } from "lucide-react";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Dialog,
  DialogContent,
  DialogTitle,
} from "@bookdental/ui";
import { trpc } from "@/lib/trpc";
import { formatFileSize } from "@/lib/files-format";
import type { FileRow } from "./files-types";

/** Busca global — nome, paciente, dentista, clínica, laboratório, serviço ou tag, tudo num campo
 *  só (o backend resolve as junções). `shouldFilter={false}` porque a lista já vem filtrada pelo
 *  servidor — o filtro fuzzy padrão do cmdk esconderia tudo (compara contra `value`, que aqui é o
 *  id do arquivo, não o texto buscado). */
export function FileSearchDialog({
  open,
  onClose,
  onDownload,
}: {
  open: boolean;
  onClose: () => void;
  onDownload: (file: FileRow) => void;
}) {
  const [query, setQuery] = useState("");
  const trimmed = query.trim();
  const searchQuery = trpc.file.search.useQuery({ query: trimmed }, { enabled: open && trimmed.length > 0 });
  const results = (searchQuery.data ?? []) as FileRow[];

  function handleClose() {
    onClose();
    setQuery("");
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent className="overflow-hidden bg-white p-0 dark:bg-card">
        <DialogTitle className="sr-only">Buscar arquivos</DialogTitle>
        <Command
          shouldFilter={false}
          className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground [&_[cmdk-group]]:px-2 [&_[cmdk-input-wrapper]_svg]:h-5 [&_[cmdk-input-wrapper]_svg]:w-5 [&_[cmdk-input]]:h-12 [&_[cmdk-item]]:px-2 [&_[cmdk-item]]:py-3"
        >
          <CommandInput
            value={query}
            onValueChange={setQuery}
            placeholder="Buscar por nome, paciente, dentista, clínica, laboratório, serviço ou tag..."
          />
          <CommandList>
            {trimmed.length === 0 ? (
              <CommandEmpty>Digite pra buscar em todos os arquivos.</CommandEmpty>
            ) : searchQuery.isFetching ? (
              <CommandEmpty>Buscando...</CommandEmpty>
            ) : results.length === 0 ? (
              <CommandEmpty>Nenhum arquivo encontrado.</CommandEmpty>
            ) : (
              <CommandGroup heading={`${results.length} resultado(s)`}>
                {results.map((file) => (
                  <CommandItem
                    key={file.id}
                    value={file.id}
                    onSelect={() => {
                      onDownload(file);
                      handleClose();
                    }}
                    className="flex items-center justify-between gap-2"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-medium">{file.name}</p>
                      {file.tags && file.tags.length > 0 && (
                        <p className="truncate text-xs text-brand-500 dark:text-primary">
                          {file.tags.map((t) => t.tag.name).join(", ")}
                        </p>
                      )}
                    </div>
                    <span className="flex shrink-0 items-center gap-1 text-xs text-muted-foreground">
                      <Download className="h-3.5 w-3.5" />
                      {formatFileSize(file.size)}
                    </span>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </DialogContent>
    </Dialog>
  );
}
